<!-- eslint-disable max-len -->
<!-- Displays an image of a currently configured product. -->

<template>
    <div
        ref="renderer"
        class="cz2__renderer absolute left-0 top-0 w-full bg-white"
        :class="{
            'cz2__renderer--new-version': newVersion,
            'cz3__renderer--locked': newVersion && rendererLockSwipe,
        }"

        key="controller">
        <div
            ref="container" class="w-full h-full relative overflow-hidden cz2__renderer__body"
        >

            <div
                class="swiper-wrapper absolute   w-full flex py-0 lg:py-0"
                :class="[isGroupOrder ?'top-16 bottom-4': 'top-4 bottom-0']"
                ref="slides">
                <div
                    v-for="view in currentVisibleTopLevelViews"
                    class="swiper-slide relative w-full flex-none"
                    :key="view.view.code"
                >
                    <renderer-image
                        :embedded="embedded"
                        :view="view.view.code"
                        :controller="view.controller"
                        @hide="hideView(view)" />

                    <renderer-image
                        v-for="otherView in view.alsoShow"
                        :key="otherView.view.code"
                        :dependent="true"
                        :embedded="embedded"
                        :view="otherView.view.code"
                        :controller="otherView.controller"
                        @hide="hideView(view)" />
                </div>
            </div>

            <div v-if="newVersion">
                <button
                    ref="buttonPrevious"
                    class="cz3__button-prev"
                    :aria-label="$t('V3_RENDER_PREV')"
                    :disabled="rendererLockSwipe"
                >
                    <img src="../assets/v2/render-left.svg" />
                </button>

                <button
                    ref="buttonNext"
                    class="cz3__button-next"
                    :aria-label="$t('V3_RENDER_NEXT')"
                    :disabled="rendererLockSwipe"
                >
                    <img src="../assets/v2/render-right.svg" />
                </button>
            </div>

            <div
                v-if="isGroupOrder" :class="[
                    'absolute bottle-picker-animation p-4 flex justify-center w-full swiper-no-swiping',
                    { 'with-toast-height': toastMessage }
                ]">
                <div class="w-64">
                    <multiselect
                        v-model="currentBottle" :searchable="false"
                        :show-labels="false" :options="allBottles"
                        :allowEmpty="false" :disabled="isGroupOrder && selectedStep === 'text'">
                        <template slot="singleLabel" slot-scope="props">
                            <span>{{ formatBottle(props) }}</span>
                        </template>
                        <template slot="option" slot-scope="props">
                            <span>{{ formatBottle(props) }}</span>
                        </template>
                    </multiselect>
                </div>
            </div>

            <template v-if="!embedded && appVersion !== '3'">
                <transition name="cz2__fade">
                    <div v-if="toastMessage" :class="['cz2__toast', 'cz2__toast--' + toastMessageType]">
                        <span class="text-sm lg:text-base text-black">{{ toastMessage }}</span>
                        <div v-if="toastSubline" class="text-sm bg-stone-900">{{ toastSubline }}</div>
                        <button
                            v-if="isToastDismissable" class="cz2__toast__clear"
                            @click.prevent="clearToast"
                            :aria-label="$t('POPUP_CLOSE')">
                        </button>
                    </div>
                </transition>
            </template>
        </div>
        <div class="flex flex-col flex-1"></div>
        <div
            ref="paginationContainer"
            class="relative w-full flex justify-center whitespace-nowrap swiper-no-swiping uppercase py-4 cz2-renderer-exclude pb-8 -mt-2"
        >
            <div v-if="appVersion !== '3'" class="absolute left-[50%] top-[10px] -translate-x-1/2 text-base">
                <span>{{showEditable && selectedSubStep === 'editing' ? $t('DESIGN_ON') : $t('DESIGN_OFF')}}</span>
            </div>

            <div class="swiper-pagination uppercase text-3xl cz__tour__view pt-2.5">
                <button
                    v-for="view in currentEditableViews"
                    :key="view.view.code"
                    class="swiper-pagination-bullet"
                    :class="{
                        'swiper-pagination-bullet-active': isSelected(view),
                    }"
                    @click.prevent="switchToView(view)"
                >
                    <span class="cz2__bullet__text">{{view.view.name}}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    @import "../style/variables.scss";

    #cz2.cz2 {
        .cz__surface {
            // Can't use transitions here since canvas has to be repainted manually.
            // transition: height $transition-fast ease-out;
        }

        .cz2__renderer {
            @include respond-below(sm) {
                height: calc(var(--app-height) - var(--exclude-height));
            }

            @include respond-above(sm) {
                // Full height minus the view selector.
                height: calc(100% - 170px);
            }

            transition: height $transition-fast ease-out;
        }

        .swiper-pagination-bullet {
            text-transform: uppercase !important;
            font-weight: 700 !important;

            @include respond-above(sm) {
                font-size: 26px !important;
            }
        }

        .cz2__renderer__mirror {
            margin-left: 20px !important;
            order: 1000;
        }

        .swiper-pagination-bullet--hidden {
            display: none !important;
        }

        .swiper-pagination-bullet {
            position: relative;
            display: inline-block;

            flex: 0 0 auto;

            width: auto;
            height: 30px;

            margin: 0;
            padding: 0;

            cursor: pointer;

            border: none;

            background: transparent;
            border-radius: 0;

            color: #000;
            font-family: $font-main;
            font-size: 18px;
            font-weight: 400;
            line-height: 30px;

            & + .swiper-pagination-bullet {
                margin-left: 20px;
            }

            &.swiper-pagination-bullet-active {
                &:after {
                    content: '';

                    position: absolute;
                    display: block;

                    left: 0;
                    right: 0;
                    bottom: 1px;
                    height: 1px;

                    background: #000;
                }
            }
        }

        .cz2__swiper-button {
            flex: 0 0 auto;

            display: flex;
            justify-content: center;
            align-items: center;

            width: 30px;
            height: 30px;
            background: transparent;

            transition: transform $transition-fast ease-out;

            &.swiper-button-disabled {
                opacity: 0.3;
            }
        }

        .cz2__swiper-button__icon {
            display: block;

            width: 8px;
            height: 13px;
        }

        .cz2__swiper-button--prev {
            margin-right: 10px;

            .cz2__swiper-button__icon {
                background: transparent url(../assets/arrow-small-left@2x.png) no-repeat center/contain;
            }
        }

        .cz2__swiper-button--next {
            margin-left: 10px;

            .cz2__swiper-button__icon {
                background: transparent url(../assets/arrow-small-right@2x.png) no-repeat center/contain;
            }
        }
    }

    #cz2.cz2 {
        .cz__render__layer {
            pointer-events: none;

            canvas {
                pointer-events: none;
            }
        }

        .bottle-picker-animation {
            transition: top $transition-fast ease-out;
            .with-toast-height {
                top: 20px + $toast-height;
            }
        }

        .cz3__button-prev, .cz3__button-next {
            position: absolute;

            flex: 0 0 auto;

            display: flex;
            justify-content: center;
            align-items: center;

            width: 40px;
            height: 40px;
            background: transparent;

            border-radius: 8px;

            transition: opacity $transition-fast ease-out;

            z-index: 2;

            &.swiper-button-disabled {
                opacity: 0.3;
            }

            &:focus {
                outline: 2px solid #000;
                outline-offset: 2px;
            }
        }

        .cz3__button-prev {
            left: 16px;
            top: 50%;
            transform: translate(0, -50%);

            @include respond-above(sm) {
                left: 40px;
            }
        }

        .cz3__button-next {
            right: 16px;
            top: 50%;
            transform: translate(0, -50%);

            @include respond-above(sm) {
                right: 40px;
            }
        }

        .cz3__renderer--locked {
            .cz3__button-prev {
                opacity: 0 !important;
            }

            .cz3__button-next {
                opacity: 0 !important;
            }
        }
    }

    #cz2.cz2.cz3--app3 {
        .cz2__renderer {
            @include respond-above(sm) {
                // Full height minus the view selector.
                height: calc(100% - 70px);
            }
        }

        .swiper-pagination-bullet {
            display: none;

            &.swiper-pagination-bullet-active {
                display: block;

                margin: 0;

                &:after {
                    display: none;
                }
            }
        }
    }
</style>

<script>
    import Vue from 'vue';

    import Swiper from 'swiper';
    import { useMediaQuery } from '@vueuse/core';
    import { mapActions, mapState } from 'vuex';
    import RendererImage from './RendererImage.vue';

    import unique from '../utilities/unique';
    import { DESKTOP_BREAKPOINT } from '../constants';

    export default {
        props: {
            embedded: Boolean,
            newVersion: Boolean,
        },
        components: {
            RendererImage,
        },
        data() {
            return {
                listenId: `id-${unique()}`,

                currentViews: [],
                currentViewsKey: null,

                title: '',

                editableViews: [],

                currentBottle: null,
            };
        },
        computed: {
            isDesktop() {
                return useMediaQuery(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
            },

            viewController() {
                return this.$store.state.customizer.viewController;
            },

            /**
             * Currently visible views.
             */
            currentVisibleViews() {
                const filtered = (this.currentViews || []).filter((v) => !v.view.errored && !v.view.productionReady && !v.view.hidden);

                return filtered;
            },

            /**
             * Currently visible views.
             */
            currentVisibleTopLevelViews() {
                let showDependent = !(this.selectedStep == null || this.selectedStep === 'group' || this.selectedStep === 'review');

                if (this.newVersion) {
                    showDependent = false;
                }

                let views = this.currentVisibleViews;

                if (!showDependent) {
                    const dependentViews = [];

                    views.forEach((v) => {
                        if (v.view.custom && v.view.custom['v-also-show']) {
                            dependentViews.push(v.view.custom['v-also-show']);
                        }
                    });

                    views = views.filter((v) => !dependentViews.includes(v.view.code));
                }

                return views;
            },

            showEditable() {
                const showEditable = !(this.selectedStep == null
                    || this.selectedStep === 'group'
                    || this.selectedStep === 'review'
                    || this.selectedStep.startsWith('color')
                );

                return showEditable;
            },

            currentEditableViews() {
                const showViews = this.showEditable && this.selectedSubStep === 'editing'
                    ? this.editableViews
                    : this.currentVisibleTopLevelViews;

                return showViews;
            },

            currentView() {
                return this.$store.state.customizer.currentView;
            },

            /**
             * Disable swiper swiping under certain circumstances.
             */
            rendererLockSwipe() {
                return this.$store.state.lockSwipe;
            },

            selectedColor() {
                return this.$store.getters.selectedColor;
            },

            appVersion() {
                return this.$store.state.appVersion;
            },

            isDesignOutOfBounds() {
                return this.$store.state.designOutside;
            },
            toastMessage() {
                if (this.$store.state.toastMessage != null) {
                    return this.$store.state.toastMessage;
                }

                if (this.isDesignOutOfBounds) {
                    return this.$t('DESIGN_OUTSIDE_WARNING');
                }

                return null;
            },
            toastSubline() {
                if (this.$store.state.toastMessage != null) {
                    return null;
                }

                if (this.isDesignOutOfBounds) {
                    return this.$t('DESIGN_OUTSIDE_WARNING_SUBLINE');
                }

                return null;
            },
            toastMessageType() {
                if (this.$store.state.toastMessageType) {
                    return this.$store.state.toastMessageType;
                }

                if (this.isDesignOutOfBounds) {
                    return 'error';
                }

                return null;
            },
            isToastDismissable() {
                return this.$store.state.toastMessage != null;
            },

            /**
             * Currently edited placements.
             */
            editPlacements() {
                return this.$store.state.customizer.editPlacements;
            },

            designOnSupported() {
                if (this.viewController) {
                    return this.viewController.blueprint.custom['v-design-on'].split(',').map((x) => x.trim());
                }

                return [];
            },

            isGroupOrder() {
                return this.$store.state.isGroupOrder;
            },

            selectedStep() {
                return this.$store.state.selectedStep;
            },

            selectedSubStep() {
                return this.$store.state.selectedSubStep;
            },

            selectedBottle() {
                return this.$store.state.customizer.selectedBottle;
            },

            /*
             * All bottles list.
             */
            allBottles() {
                return this.$store.state.customizer.secondaryBottles.map((bottle, index) => ({
                    bottle,
                    index,
                }));
            },

            showMobileOrderTypePrompt() {
                return this.$store.state.showMobileOrderTypePrompt;
            },

            supportsMirroring() {
                return this.$store.getters.supportsMirroring;
            },
        },
        watch: {
            /**
             * Load image view for the new controller.
             */
            viewController(newController, oldController) {
                this.disconnect(oldController);

                this.load(newController);
            },

            /**
             * Enables or disables manual interactions with the swiper.
             */
            rendererLockSwipe() {
                if (this.swiper) {
                    this.swiper.allowTouchMove = !this.rendererLockSwipe;
                }
            },

            /**
             * Update toast message.
             */
            toastMessage() {
                if (this.toastMessage) {
                    this.toastTimer = setTimeout(() => {
                        this.$store.dispatch('toast', null);
                        this.toastTimer = null;
                    }, 15000);
                } else if (this.toastTimer) {
                    clearTimeout(this.toastTimer);
                    this.toastTimer = null;
                }
            },

            /**
             * Shows editable views only.
             */
            editPlacements() {
                this.updateBullets();
            },

            /**
             * Attempts to reselect the bottle after the bottles list changes.
             */
            allBottles() {
                if (this.allBottles && this.currentBottle) {
                    // Is the current bottle no longer valid?
                    if (!this.allBottles.includes(this.currentBottle)) {
                        const sameIndex = this.allBottles.find((b) => b.index === this.currentBottle.index);

                        if (sameIndex != null) {
                            this.currentBottle = sameIndex;
                        } else if (this.allBottles.length > 0) {
                            // eslint-disable-next-line prefer-destructuring
                            this.currentBottle = this.allBottles[0];
                        }
                    }
                }
            },

            /**
             * Updates the stored bottle reference after selection changes.
             */
            selectedBottle() {
                this.currentBottle = this.allBottles.find((b) => b.bottle === this.selectedBottle);

                // In the embedded mode, we are working with a cloned controller.
                // If the bottle selection has changed, we won't see any placement updates since those
                // are happening in the viewController.
                // So, re-mount a controller after the selection change.
                this.$nextTick(() => {
                    this.mountController(this.viewController);
                });
            },

            currentBottle() {
                if (this.currentBottle && this.currentBottle.bottle !== this.selectedBottle) {
                    this.$store.dispatch('setSelectedBottle', this.currentBottle.bottle);
                }
            },

            /**
             * Update slides and bullets when step changes.
             */
            selectedStep() {
                this.updateViews();
            },
        },
        mounted() {
            // Resize the view when window size changes.
            this.resize();
            window.addEventListener('resize', this.resize);

            this.resizeObserver = new ResizeObserver(() => {
                this.resize();
            });

            this.resizeObserver.observe(this.$el);

            // Paint the main controller.
            if (this.viewController) {
                this.mountController(this.viewController);
            }

            this.$store.state.eventBus.$on('validateEditableViews', this.validateEditableViews);
            this.$store.state.eventBus.$on('switchToView', this.switchToView);

            // Ensure the bottle selection.
            if (this.selectedBottle) {
                this.currentBottle = this.allBottles.find((b) => b.bottle === this.selectedBottle);
            }
        },
        beforeDestroy() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }

            window.removeEventListener('resize', this.resize);

            if (this.clonedController) {
                this.clonedController.off();
            }

            // Destroy swiper after any external transitions have ran.
            setTimeout(() => {
                if (this.swiper) {
                    this.swiper.destroy();
                    this.swiper = null;
                }
            }, 500);
        },
        methods: {
            /**
             * Resizes image view.
             */
            resize() {
                // Might not have rerendered yet.
                if (this.$refs.renderer == null) {
                    this.$nextTick(() => {
                        this.resize();
                    });

                    return;
                }

                this.$store.dispatch('setRendererHeight', this.$refs.renderer.offsetHeight);
            },

            ...mapActions({
                selectSubStep: 'selectSubStep',
            }),

            /**
             * Disconnects image view from the controller and stops listening to events.
             */
            disconnect(controller) {
                if (controller) {
                    controller.off(this.embedded ? '.renderer-embedded' : '.renderer');
                }
            },

            /**
             * Loads image view for the given controller.
             */
            load(controller) {
                if (controller) {
                    this.mountController(controller);
                }
            },

            /**
             * Create image view.
             */
            mountController(newController) {
                if (newController?.blueprint == null) {
                    return;
                }

                let controller = newController;

                if (this.embedded) {
                    if (this.clonedController) {
                        this.clonedController.off();
                    }

                    controller = newController.clone();
                    controller.off('.renderer');

                    this.clonedController = controller;
                }

                // Find views to display.
                let { views } = controller.blueprint;

                views = views.filter((view) => view.custom?.['v-hidden'] !== 'true').map((view) => ({
                    view,
                    controller,
                    alsoShow: [],
                }));

                const dependentViews = [];

                views.forEach((v) => {
                    if (v.view.custom && v.view.custom['v-also-show']) {
                        dependentViews.push(v.view.custom['v-also-show']);
                    }
                });

                views.forEach((v) => {
                    v.dependent = dependentViews.includes(v.view.code);
                });

                views.forEach((v) => {
                    if (v.view.custom && v.view.custom['v-also-show']) {
                        views.forEach((otherV) => {
                            if (otherV.view.code === v.view.custom['v-also-show']) {
                                v.alsoShow.push(otherV);
                            }
                        });
                    }
                });

                const { styleCode } = controller.blueprint;

                const viewsKey = views.map((v) => `${styleCode}-${v.view.code}`).join(':');

                // If current views are compatible, simply adopt the new controller.
                if (this.currentViewsKey === viewsKey) {
                    this.currentViews.forEach((view) => {
                        view.controller = controller;
                    });

                    this.updateViews();

                    this.resize();
                } else {
                    this.currentViews = views;
                    this.currentViewsKey = viewsKey;

                    this.$nextTick(() => {
                        this.createSwiper();

                        this.updateViews();
                    });

                    this.resize();
                }

                // Take over editable placements.
                controller.on(this.embedded ? 'placementHidden.renderer-embedded' : 'placementHidden.renderer', ({ placement, view, baking }) => {
                    if (baking) {
                        return undefined;
                    }

                    if (view.productionReady) {
                        return undefined;
                    }

                    if (!this.embedded) {
                        if (placement.placement.custom && placement.placement.custom['layout-editor'] === 'true') {
                            return true;
                        }
                    }

                    return undefined;
                });

                // After everything was calculated revalidated editable views.
                this.$nextTick(() => {
                    this.validateEditableViews();
                });
            },

            /**
             * Updates view slides visibility.
             */
            updateViews() {
                let activeSlide = 0;

                if (this.swiper) {
                    activeSlide = this.swiper.realIndex;
                }

                const showDependent = false;

                const slides = Array.from(this.$refs.slides.querySelectorAll('[data-dependent]'));

                let selectSlide = null;

                let x = 0;
                let lastWidth = 0;

                slides.forEach((s, index) => {
                    const dependent = s.dataset.dependent === 'true';

                    if (!dependent || (showDependent && dependent)) {
                        s.style.position = 'relative';
                        s.style.left = '0px';
                        s.style.top = '0px';
                        s.classList.add('swiper-slide');
                        x += s.offsetWidth;
                        lastWidth = s.offsetWidth;
                    // s.querySelector('.cz2__image').style.opacity = '1';
                    } else {
                        s.style.position = 'absolute';
                        s.style.left = `${x - lastWidth}px`;
                        s.style.top = '0px';
                        s.style.width = `${lastWidth}px`;
                        s.style.height = '100%';
                        s.classList.remove('swiper-slide');
                        // s.querySelector('.cz2__image').style.opacity = '0';

                        if (index === activeSlide) {
                            if (activeSlide > 0) {
                                selectSlide = activeSlide - 1;
                            }
                        }
                    }
                });

                if (this.swiper) {
                    this.swiper.update();

                    if (selectSlide != null) {
                        this.swiper.slideTo(selectSlide);
                    }
                }

                this.updateBullets();
            },

            /**
             * Creates swipable surface.
             */
            createSwiper() {
                if (this.swiper) {
                    this.swiper.destroy();
                    this.swiper = null;
                }

                if (this.currentViews.length > 1) {
                    const swiperOptions = {
                        slidesPerView: 1,
                        on: {
                            slideChange: () => {
                                this.updateTitle();
                                this.updateBullets();
                            },
                        },
                        pagination: {
                            el: this.$refs.pagination,
                            clickable: true,
                            renderBullet: (index, className) => this.getBullet(index, className),
                        },
                        navigation: {
                            nextEl: this.$refs.buttonNext,
                            prevEl: this.$refs.buttonPrevious,
                        },
                    };

                    this.swiper = new Swiper(this.$refs.container, swiperOptions);

                    this.swiper.allowTouchMove = !this.rendererLockSwipe;

                    this.updateTitle();
                    this.updateBullets();
                }
            },

            /**
             * Render bullet.
             */
            getBullet(index, className) {
                const view = this.currentVisibleViews[index];

                if (view) {
                    // eslint-disable-next-line
                return `<button class="${className}" data-index="${index}"><span class="cz2__bullet__text">${view.view.name}</span></button>`;
                }

                return `<button class="${className}"></button>`;
            },

            /**
             * Automatically swipes to the selected view.
             */
            swipeTo(code) {
                if (this.swiper && this.currentViews) {
                    const viewIndex = this.currentViews.findIndex((v) => v.view.code === code);
                    if (viewIndex >= 0) {
                        this.swiper.slideTo(viewIndex);
                    }
                }
            },

            /**
             * Hides a view.
             */
            hideView(view) {
                Vue.set(view.view, 'errored', true);

                view.view.continueOnError = true;

                // eslint-disable-next-line
            console.log('Hide view', view.view.code);

                this.$nextTick(() => {
                    this.createSwiper();
                });
            },

            /**
             * Updates current slide title.
             */
            updateTitle() {
                let activeSlide = 0;

                if (this.swiper) {
                    activeSlide = this.swiper.realIndex;
                }

                if (this.currentVisibleTopLevelViews && activeSlide < this.currentVisibleTopLevelViews.length) {
                    const view = this.currentVisibleTopLevelViews[activeSlide];

                    // Is current view already selected OR somehow related.
                    let select = true;

                    if (view && this.currentView) {
                        if (view.view.code === this.currentView.view.code) {
                            select = false;
                        }

                        if (view.alsoShow.some((x) => x.view.code === this.currentView.view.code)) {
                            select = false;
                        }
                    }

                    if (select) {
                        this.$store.dispatch('setCurrentView', view);
                    }

                    this.title = view.view.name;
                }
            },

            /**
             * Clear toast notification.
             */
            clearToast() {
                this.$store.dispatch('toast', null);
            },

            /**
             * Figures out which views are editable.
             */
            validateEditableViews() {
                if (this.viewController) {
                    const designOn = this.viewController.blueprint.custom['v-design-on'];

                    if (designOn) {
                        const views = designOn.split(',').map((x) => x.trim());

                        this.editableViews = this.currentVisibleViews.filter((v) => {
                            if (v.view.custom && v.view.custom['v-design-area'] && views.includes(v.view.custom['v-design-area'])) {
                                return true;
                            }

                            return false;
                        });

                        let activeSlide = 0;

                        if (this.swiper) {
                            activeSlide = this.swiper.realIndex;
                        }

                        if (this.currentVisibleViews && activeSlide < this.currentVisibleViews.length) {
                            const view = this.currentVisibleViews[activeSlide];

                            if (!this.editableViews.includes(view) && this.editableViews.length > 0) {
                                const switchToView = this.editableViews[0];

                                const switchToIndex = this.currentVisibleViews.indexOf(switchToView);

                                this.swiper.slideTo(switchToIndex);

                                this.$store.dispatch('setCurrentView', switchToView);
                            }
                        }
                    }
                }
            },

            /**
             * Shows bullets only for editable views.
             */
            updateBullets() {
                if (this.$refs.pagination) {
                    const showViews = this.editPlacements && this.editPlacements.length > 0 ? this.editableViews : this.currentVisibleViews;

                    let mirrored = false;
                    if (this.editPlacements && this.editPlacements.length > 0) {
                        if (this.editPlacements[0].custom['v-mirror-to']) {
                            mirrored = true;
                        }
                    }

                    const bullets = Array.from(this.$refs.paginationContainer.querySelectorAll('.swiper-pagination-bullet'));

                    let offset = 0;

                    bullets.forEach((bullet, index) => {
                        if (bullet.dataset.mirror === 'true') {
                            offset += 1;
                        } else {
                            const view = this.currentVisibleTopLevelViews[index - offset];

                            if (view) {
                                bullet.textContent = view.view.name;
                            }

                            if (this.editPlacements && this.editPlacements.length > 0) {
                                if (view.view.custom && view.view.custom['v-editor-title']) {
                                    bullet.textContent = view.view.custom['v-editor-title'];
                                }
                            }

                            if (showViews.includes(view)) {
                                bullet.classList.remove('swiper-pagination-bullet--hidden');
                            } else {
                                bullet.classList.add('swiper-pagination-bullet--hidden');
                            }
                        }
                    });

                    if (mirrored) {
                        this.$refs.paginationContainer.classList.add('cz2__renderer__controls--with-mirror');
                    } else {
                        this.$refs.paginationContainer.classList.remove('cz2__renderer__controls--with-mirror');
                    }
                }
            },

            /**
             * Is view selected?
             */
            isSelected(view) {
                if (view && this.currentView) {
                    if (this.currentView.view.code === view.view.code) {
                        return true;
                    }

                    if (!this.showEditable) {
                        if (view.alsoShow && view.alsoShow.some((x) => x.view.code === this.currentView.view.code)) {
                            return true;
                        }
                    }
                }

                return false;
            },

            /**
             * Switches to the requested view.
             */
            switchToView(view) {
                if (view) {
                    let switchToIndex = null;

                    this.currentVisibleTopLevelViews.forEach((v, index) => {
                        if (v.view.code === view.view.code) {
                            switchToIndex = index;
                        }

                        if (v.alsoShow.some((x) => x.view.code === view.view.code)) {
                            switchToIndex = index;
                        }
                    });

                    if (switchToIndex >= 0 && this.swiper) {
                        this.$store.dispatch('setCurrentView', view);

                        this.swiper.slideTo(switchToIndex);
                    }
                }
            },

            /**
             * Formats a bottle.
             */
            formatBottle(v) {
                // For some reason, vue-multiselect sometimes unwinds options that are array of a single item,
                // so this code has to handle both situations.

                let text = v.option.bottle == null || v.option.bottle.length === 0 ? this.$t('BOTTLE_EMPTY') : v.option.bottle;

                if (text.join) {
                    text = text.filter((t) => t != null && t.length > 0).join(', ');
                }

                if (text.length === 0) {
                    text = this.$t('BOTTLE_EMPTY');
                }

                return `${this.$t('BOTTLE')} ${v.option.index + 1}: ${text}`;
            },

            /**
             * Selects a view.
             */
            selectView(e) {
                if (e && e.target) {
                    if (!e.target.classList.contains('cz2__renderer__mirror')) {
                        if (this.editPlacements && this.editPlacements.length > 0) {
                            // Use had selected a view explicitly and we need to turn off mirroring.
                            this.$store.dispatch('configureMirroring', {
                                mirror: false,
                                placements: this.editPlacements,
                            });
                        }

                        this.updateBullets();
                    }
                }
            },

            /**
             * Move design to the mirror mode.
             */
            selectMirror() {
                if (this.editPlacements && this.editPlacements.length > 0) {
                    this.$store.dispatch('configureMirroring', {
                        mirror: true,
                        placements: this.editPlacements,
                    });

                    this.updateBullets();

                    // Switch to the front view.
                    if (this.currentVisibleViews) {
                        const view = this.currentVisibleViews.find((v) => v.view.code === 'Front');

                        if (view) {
                            const switchToView = this.editableViews[0];

                            const switchToIndex = this.currentVisibleViews.indexOf(switchToView);

                            this.swiper.slideTo(switchToIndex);

                            this.$store.dispatch('setCurrentView', switchToView);
                        }
                    }
                }
            },
        },
    };
</script>
